import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import ReactGA from "react-ga";


// Material-UI imports
import {
  Button,
  Typography,
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { SECONDARYCOLOR } from '../components/helpers/ThemeSettings';

// Third-party library imports
import { Flex } from "reflexbox";

// Local component imports
import SettingsMenu from "../components/gameSetupAndLobby/SettingsMenu";
import SkeletonImage from "../components/common/SkeletonImage";
import TeamDetailsBottomSheet from "../components/gameSetupAndLobby/TeamDetailsBottomSheet";
import PersonalProfileDisplayAndEdit from "../components/PersonalProfileDisplayAndEdit";
import CopyItem from "../components/common/CopyItem";
import emojiOptions from "../helpers/emojiOptions";

// Utility/helper imports
import { courses, experiences } from "../components/courseContent/courseObjects";
import { writeTeamProgress, leaveTeamOrDeleteIfSolo } from "../helpers/dbFunctions";
import { isProd } from "../components/helpers/misc";
import * as route from "../components/helpers/routes";
import { ToQuestion } from "../components/helpers/routes";
import Logo from "../images/capital-clues-logo.png";
import PageHeader from '../components/gamePlayComponents/PageHeader';
import { resetTeam } from '../redux/teamSlice';
import { createAlert, setShareJoinCodePromptTeamId } from '../redux/uiSlice';
import { messageType } from "../components/common/RequestAlertBar";

const Lobby = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [teamDetailsSheetOpen, setTeamDetailsSheetOpen] = useState(false);
  const [shareJoinCodeDialogOpen, setShareJoinCodeDialogOpen] = useState(false);
  const [hostLeftDialogOpen, setHostLeftDialogOpen] = useState(false);

  const { uid, userIntent } = useSelector((state) => state.user);
  const { currentTeamId, teamDetails } = useSelector((state) => state.team);
  const shareJoinCodePromptTeamId = useSelector((state) => state.ui?.shareJoinCodePromptTeamId);
  const { currentCourseSelection = '', courseProgress = '', teammateInfo = {}, hostId, memberIds = [] } = teamDetails || {};
  const isSolo = userIntent === 'SOLO';
  // Only host can edit course or start game; for legacy teams without hostId, allow everyone
  const isHost = isSolo || (hostId != null ? uid === hostId : true);

  const teamLength = teammateInfo ? Object.keys(teammateInfo).length : 0;
  const selectedCourse = currentCourseSelection ? courses[currentCourseSelection] : null;
  const startOption = selectedCourse?.startOption ?? null;
  const experienceName = selectedCourse?.experienceId
    ? experiences[selectedCourse.experienceId]?.name
    : null;
  const showExperienceName = experienceName
    && experienceName.toLowerCase() !== selectedCourse?.friendlyCourseName?.toLowerCase();

  const previousTeammateInfoRef = useRef(null);
  const hasInitializedTeammatesRef = useRef(false);

  useEffect(() => {
    if (!isSolo && isHost && currentTeamId && shareJoinCodePromptTeamId !== currentTeamId) {
      setShareJoinCodeDialogOpen(true);
      dispatch(setShareJoinCodePromptTeamId(currentTeamId));
    }
  }, [currentTeamId, shareJoinCodePromptTeamId]);

  // Snackbar alerts when teammates join or leave the team
  useEffect(() => {
    //don't spam alerts if team is being torn down 
    if (!teammateInfo || !currentTeamId) {
      previousTeammateInfoRef.current = teammateInfo;
      return;
    }

    const previousTeammateInfo = previousTeammateInfoRef.current || {};
    const previousIds = Object.keys(previousTeammateInfo || {});
    const currentIds = Object.keys(teammateInfo || {});

    // Skip firing alerts on the very first initialization
    if (!hasInitializedTeammatesRef.current) {
      hasInitializedTeammatesRef.current = true;
      previousTeammateInfoRef.current = teammateInfo;
      return;
    }

    const newIds = currentIds.filter((id) => !previousIds.includes(id));
    const leftIds = previousIds.filter((id) => !currentIds.includes(id));

    newIds.forEach((id) => {
      if (id === uid) return;
      const info = teammateInfo[id] || {};
      const name = info.name || 'A teammate';
      dispatch(createAlert({
        message: `${name} joined the team`,
        type: messageType.success,
      }));
    });

    leftIds.forEach((id) => {
      if (id === uid) return;
      const info = previousTeammateInfo[id] || {};
      const name = info.name || 'A teammate';
      dispatch(createAlert({
        message: `${name} left the team`,
        type: messageType.warning,
      }));
    });

    previousTeammateInfoRef.current = teammateInfo;
  }, [dispatch, teammateInfo, uid]);

  // If the host leaves the team, inform remaining teammates and return them to join/host options
  useEffect(() => {
    const memberIdsArr = Array.isArray(memberIds) ? memberIds : Object.values(memberIds || {});
    if (
      !isSolo &&
      !isHost &&
      hostId &&
      currentTeamId &&
      memberIdsArr.length > 0 &&
      !memberIdsArr.includes(hostId)
    ) {
      setHostLeftDialogOpen(true);
    }
  }, [isSolo, isHost, hostId, currentTeamId, memberIds]);

  useEffect(() => {
    if (currentCourseSelection && courseProgress?.[currentCourseSelection]?.includes('0')) {
      handleStart();
    }
  }, [courseProgress]);

  const nextURL = ToQuestion(currentCourseSelection, '0');

  const handleStart = () => {
    //fire google analytics event
    isProd && ReactGA.event({
      category: 'Course Started',
      action: `Started ${currentCourseSelection}`
    });
    //set rtdb course progress
    !courseProgress?.[currentCourseSelection] && writeTeamProgress(currentTeamId, currentCourseSelection, nextURL);
    navigate(nextURL);
  };

  const handleHostLeftAcknowledge = async () => {
    setHostLeftDialogOpen(false);
    if (currentTeamId) {
      await leaveTeamOrDeleteIfSolo(uid, currentTeamId);
    }
    dispatch(resetTeam());
    navigate(route.JOINORHOST);
  };

  return (
    <div>
      {/* Header */}
    <PageHeader
        title="Ready to start?"
        goHome
        goHomeLabel={!isSolo ? 'Leave team' : undefined}
        onGoHome={async () => {
          await leaveTeamOrDeleteIfSolo(uid, currentTeamId);
          dispatch(resetTeam());
          navigate(route.HOME);
        }}
      />

      {/* Host Join Code Share Prompt */}
      <Dialog
        open={shareJoinCodeDialogOpen && !isSolo && isHost}
        onClose={() => setShareJoinCodeDialogOpen(false)}
      >
        <DialogTitle>Share your join code</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', letterSpacing: '2px', mr: 1 }}
            >
              {currentTeamId}
            </Typography>
            <CopyItem thingToCopy={currentTeamId} />
          </Box>
          <Typography variant="body1" gutterBottom>
            Share this code with your teammates so they can join your game from their devices.
          </Typography>
          
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShareJoinCodeDialogOpen(false)}
            color="primary"
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Host Left Team Notification */}
      <Dialog
        open={hostLeftDialogOpen}
        onClose={handleHostLeftAcknowledge}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', pt: 2 }}>
          <SentimentDissatisfiedIcon style={{ color: SECONDARYCOLOR }} fontSize="large" />
        </Box>
        <DialogTitle style={{ color: SECONDARYCOLOR }}>
          Host left the team
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The host has left this team, so this game is no longer available. You&apos;ll be returned to the menu where you can choose to join an existing team or host a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleHostLeftAcknowledge}
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Participants Section – hidden for solo play */}
      {!isSolo && (
        <>
          <Box style={{marginBottom: '24px'}}>
            <Typography variant="h5" >
              Team
            </Typography>
            <Flex row >
              <Typography>{teamLength} player{teamLength !== 1 && 's'}</Typography>
              <Flex style={{alignItems: 'center', flexWrap: 'wrap'}}>
                <Typography style={{paddingLeft: '8px'}}>
                ·  Join code: <span style={{fontWeight: 'bold', letterSpacing: '2px'}}>{currentTeamId}</span>
                </Typography>
                <CopyItem thingToCopy={currentTeamId}/>
              </Flex>
            </Flex>
            {teammateInfo && Object.keys(teammateInfo).length > 0 && (
              <Flex row style={{ alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {Object.entries(teammateInfo).map(([teammatesUid, memberInfo]) => (
                  <Avatar
                    key={teammatesUid}
                    style={memberInfo.color ? { backgroundColor: memberInfo.color } : undefined}
                  >
                    {memberInfo.character >= 0 && emojiOptions[memberInfo.character] ? (
                      emojiOptions[memberInfo.character]
                    ) : (
                      (memberInfo.name || '?')[0]
                    )}
                  </Avatar>
                ))}
              </Flex>
            )}
            <Button startIcon={<GroupsIcon />} onClick={() => setTeamDetailsSheetOpen(true)}>
              View/Edit Details
            </Button>
          </Box>

          <hr/>
        </>
      )}

      <TeamDetailsBottomSheet
        open={teamDetailsSheetOpen}
        onClose={() => setTeamDetailsSheetOpen(false)}
        onEditProfile={() => setProfileEditOpen(true)}
      />
      <PersonalProfileDisplayAndEdit
        open={profileEditOpen}
        close={() => setProfileEditOpen(false)}
      />

      {/* Course Selection Section */}
      <Box style={{marginBottom: '24px'}}>
        <Typography variant="h5" sx={{mb: 1}}>
          Course
        </Typography>
        {currentCourseSelection ? (
          <Flex style={{alignItems: 'flex-start', gap: '16px'}}>
            {selectedCourse?.coverPhoto && (
              <Box sx={{ flexShrink: 0 }}>
                <SkeletonImage
                  src={selectedCourse.coverPhoto}
                  alt={selectedCourse?.friendlyCourseName || 'Course image'}
                  imgWidth="135px"
                  imgHeight="auto"
                  skeletonWidth="135px"
                  skeletonHeight="81.1px"
                  style={{ height: 'auto' }}
                />
              </Box>
            )}
            <Flex style={{alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: 'column', flex: 1, minWidth: 0, gap: '4px'}}>
              {showExperienceName && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{experienceName}</Typography>
              )}
              <Typography color={showExperienceName ? 'text.secondary' : 'inherit'} variant={showExperienceName ? 'body2' : 'body1'}>
                {selectedCourse?.friendlyCourseName}
              </Typography>
              <Button 
                color="primary" 
                onClick={() => navigate(isHost ? route.COURSESELECTOR_HOST : `${route.COURSEVIEWER_LOBBY}?course=${currentCourseSelection}`)}
                size="small"
                startIcon={isHost ? <EditIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
              >
                {isHost ? "Change course" : "View details"}
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate(route.COURSESELECTOR_HOST)}
            disabled={!currentTeamId || !isHost}
          >
            Select Course
          </Button>
        )}
      </Box>

      <hr/>

      {/* Starting Point Section */}
      <Box style={{marginBottom: '24px'}}>
        <Typography variant="h5" >
          Starting Point
        </Typography>
        {startOption ? (
          <Flex alignItems={'center'} style={{padding: "8px 0"}}>
            <img 
              src={startOption.icon} 
              alt={startOption.name} 
              style={{width: '60px', marginRight: '12px'}}
            />
            <Flex flexDirection={'column'}>
              <Typography>{startOption.name}</Typography>
              {startOption.googleMapsLink && (
                <Button
                  component="a"
                  href={startOption.googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  color="primary"
                  size="small"
                  startIcon={<LocationOnIcon fontSize="small" />}
                  style={{marginTop: '4px', alignSelf: 'flex-start'}}
                >
                  Get directions
                </Button>
              )}
            </Flex>
          </Flex>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a course to see starting point
          </Typography>
        )}
      </Box>

      {/* Start Button */}
      <Box style={{marginTop: '32px'}}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={!isHost || !currentCourseSelection || (!isSolo && teamLength === 0)}
          onClick={handleStart}
          style={{padding: '12px'}}
        >
         {isHost ? "Start Game" : "Waiting on host to start"}
        </Button>
      </Box>
    </div>
  );
};

export default Lobby;
